import dayjs from 'dayjs';
export var MAX_COLUMNS_PER_PAGE = 31;
// Hard ceiling on how many columns any single unit can ever produce (~27 years of daily
// columns). This is a last-resort safety net, not a normal limit — it exists so that any
// unforeseen edge case (rather than just the known invalid-date case, already guarded in
// buildTimeColumns) can never turn a column-building loop into an unbounded one.
var MAX_TIME_COLUMNS = 10000;
var CELL_UNITS = ['daily', 'weekly', 'monthly', 'quarterly'];
function toDay(value) {
    return dayjs(value).startOf('day');
}
function toDate(value) {
    return value.toDate();
}
export function periodsOverlap(periodStart, periodEnd, rangeStart, rangeEnd) {
    var pStart = toDay(periodStart);
    var pEnd = toDay(periodEnd);
    var rStart = toDay(rangeStart);
    var rEnd = toDay(rangeEnd);
    return !pStart.isAfter(rEnd, 'date') && !pEnd.isBefore(rStart, 'date');
}
function buildDailyColumns(rangeStart, rangeEnd) {
    var columns = [];
    var current = rangeStart;
    while (!current.isAfter(rangeEnd, 'date') && columns.length < MAX_TIME_COLUMNS) {
        columns.push({
            startDate: toDate(current),
            endDate: toDate(current),
            label: current.format('D'),
            groupLabel: current.format('M月')
        });
        current = current.add(1, 'day');
    }
    return columns;
}
function buildWeeklyColumns(rangeStart, rangeEnd) {
    var columns = [];
    var sunday = rangeStart.day(0);
    if (sunday.isAfter(rangeStart, 'date')) {
        sunday = sunday.subtract(7, 'day');
    }
    while (!sunday.isAfter(rangeEnd, 'date') && columns.length < MAX_TIME_COLUMNS) {
        var saturday = sunday.add(6, 'day');
        if (periodsOverlap(sunday, saturday, rangeStart, rangeEnd)) {
            columns.push({
                startDate: toDate(sunday),
                endDate: toDate(saturday),
                label: sunday.format('D') + "\u65E5",
                groupLabel: sunday.format('YYYY年M月')
            });
        }
        sunday = sunday.add(7, 'day');
    }
    return columns;
}
function buildMonthlyColumns(rangeStart, rangeEnd) {
    var columns = [];
    var current = rangeStart.startOf('month');
    while (!current.isAfter(rangeEnd, 'date') && columns.length < MAX_TIME_COLUMNS) {
        var monthStart = current.startOf('month');
        var monthEnd = current.endOf('month');
        if (periodsOverlap(monthStart, monthEnd, rangeStart, rangeEnd)) {
            columns.push({
                startDate: toDate(monthStart),
                endDate: toDate(monthEnd),
                label: current.format('M') + "\u6708",
                groupLabel: current.format('YYYY') + "\u5E74"
            });
        }
        current = current.add(1, 'month').startOf('month');
    }
    return columns;
}
function getQuarterBounds(year, quarter) {
    var startMonth = (quarter - 1) * 3;
    var start = dayjs(new Date(year, startMonth, 1)).startOf('day');
    var end = start.add(2, 'month').endOf('month').startOf('day');
    return { start: start, end: end };
}
function buildQuarterlyColumns(rangeStart, rangeEnd) {
    var columns = [];
    var year = rangeStart.year();
    var endYear = rangeEnd.year();
    while (year <= endYear && columns.length < MAX_TIME_COLUMNS) {
        for (var quarter = 1; quarter <= 4; quarter += 1) {
            var _a = getQuarterBounds(year, quarter), start = _a.start, end = _a.end;
            if (end.isBefore(rangeStart, 'date')) {
                continue;
            }
            if (start.isAfter(rangeEnd, 'date')) {
                break;
            }
            if (periodsOverlap(start, end, rangeStart, rangeEnd)) {
                columns.push({
                    startDate: toDate(start),
                    endDate: toDate(end),
                    label: "Q" + quarter,
                    groupLabel: year + "\u5E74"
                });
            }
        }
        year += 1;
    }
    return columns;
}
export function buildTimeColumns(start, end, unit) {
    var rangeStart = toDay(start);
    var rangeEnd = toDay(end);
    // An invalid date (e.g. a project with no end_date, or a malformed query param) makes every
    // isAfter/isBefore comparison below return false, which would otherwise turn each column
    // builder's while loop into an infinite loop that never stops appending columns.
    if (!rangeStart.isValid() || !rangeEnd.isValid()) {
        return [];
    }
    if (rangeEnd.isBefore(rangeStart, 'date')) {
        return [];
    }
    switch (unit) {
        case 'daily':
            return buildDailyColumns(rangeStart, rangeEnd);
        case 'weekly':
            return buildWeeklyColumns(rangeStart, rangeEnd);
        case 'monthly':
            return buildMonthlyColumns(rangeStart, rangeEnd);
        case 'quarterly':
            return buildQuarterlyColumns(rangeStart, rangeEnd);
        default:
            return buildDailyColumns(rangeStart, rangeEnd);
    }
}
export function countColumns(start, end, unit) {
    return buildTimeColumns(start, end, unit).length;
}
export function suggestCellUnit(start, end) {
    for (var _i = 0, CELL_UNITS_1 = CELL_UNITS; _i < CELL_UNITS_1.length; _i++) {
        var unit = CELL_UNITS_1[_i];
        if (countColumns(start, end, unit) <= MAX_COLUMNS_PER_PAGE) {
            return unit;
        }
    }
    return 'quarterly';
}
export function getColumnSpan(processStart, processEnd, columns) {
    if (!columns.length) {
        return null;
    }
    var startIndex = -1;
    var endIndex = -1;
    columns.forEach(function (column, index) {
        if (!periodsOverlap(column.startDate, column.endDate, processStart, processEnd)) {
            return;
        }
        if (startIndex === -1) {
            startIndex = index;
        }
        endIndex = index;
    });
    if (startIndex === -1) {
        return null;
    }
    return { startIndex: startIndex, endIndex: endIndex };
}
export function findColumnIndexForDate(date, columns) {
    var target = toDay(date);
    return columns.findIndex(function (column) {
        return periodsOverlap(column.startDate, column.endDate, target, target);
    });
}
export function clampDateRangeToProject(dateRange, projectStart, projectEnd) {
    var start = dateRange[0], end = dateRange[1];
    var pStart = toDay(projectStart);
    var pEnd = toDay(projectEnd);
    // A project without a defined end_date (a common, valid case for an ongoing project) or a
    // malformed saved/query date otherwise makes toDay(...) produce an invalid dayjs. Formatting
    // that below would silently return the literal string "Invalid Date", which — if it ends up
    // in a URL query param or saved print setting — makes buildTimeColumns loop forever.
    var fallbackStart = pStart.isValid() ? pStart : dayjs().startOf('day');
    var fallbackEnd = pEnd.isValid() ? pEnd : fallbackStart;
    var clampedStart = toDay(start);
    var clampedEnd = toDay(end);
    if (!clampedStart.isValid()) {
        clampedStart = fallbackStart;
    }
    if (!clampedEnd.isValid()) {
        clampedEnd = fallbackEnd;
    }
    if (pStart.isValid() && clampedStart.isBefore(pStart, 'date')) {
        clampedStart = pStart;
    }
    if (pEnd.isValid() && clampedEnd.isAfter(pEnd, 'date')) {
        clampedEnd = pEnd;
    }
    if (clampedEnd.isBefore(clampedStart, 'date')) {
        clampedStart = fallbackStart;
        clampedEnd = fallbackEnd;
    }
    return [clampedStart.format('YYYY-MM-DD'), clampedEnd.format('YYYY-MM-DD')];
}
